import React, { Component, RefObject, createRef } from 'react';
import Cropper from 'react-cropper';
import { DocumentNode } from 'graphql';
import { compose } from 'recompose';
import { Trans } from 'react-i18next';
import { MutationFunction } from 'react-apollo';

import {
  Button,
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import { S3ObjectInput } from 'api';
import { AspectRatios } from 'types/app';
import { withUser, IWithUser } from 'providers/UserContext';
import { SubmitButton, UploadInput, IUploader } from '../';
import { generateS3Image } from 'helpers';

import Mutation from './Mutation';

interface IProps extends IUploader {
  i18nKey: string;
  aspectRatio: AspectRatios;
  onSave: (image: S3ObjectInput, mutation?: MutationFunction) => void;
  mutation?: DocumentNode;
}

interface IPropsEnhanced extends IProps, IWithUser, WithStyles<typeof styles> {}

const styles = (theme: Theme) =>
  createStyles({
    cropper: {
      height: 400,
    },
    static: {
      width: '100%',
    },
    container: {
      marginTop: theme.spacing(4),
    },
    input: {
      display: 'none',
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(withUser, withStyles(styles));

class Dialog extends Component<IPropsEnhanced> {
  cropper: RefObject<any>;

  constructor(props: IPropsEnhanced) {
    super(props);

    this.cropper = createRef();
  }

  isGif() {
    const { data } = this.props;
    const { file } = data;

    return file && file.type === 'image/gif';
  }

  getImage(blob: Blob) {
    const { data, user } = this.props;
    const { file } = data;

    return generateS3Image(blob, file, user);
  }

  handleSave = (mutationFn?: MutationFunction) => {
    const { data, onSave } = this.props;
    const { file } = data;

    if (this.isGif()) {
      return onSave(this.getImage(file), mutationFn);
    }

    const cropper = this.cropper.current;
    const canvas = cropper.getCroppedCanvas();
    const { height, width } = canvas;

    canvas.toBlob((blob: Blob) => {
      const image = {
        height,
        width,
        ...this.getImage(blob),
      };

      onSave(image, mutationFn);
    });
  };

  renderTitle = () => {
    const { i18nKey, onClose } = this.props;

    return (
      <DialogTitle disableTypography>
        <Typography variant="subtitle2">
          <Trans i18nKey={i18nKey} />
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
    );
  };

  renderSubmit() {
    const { onClose } = this.props;

    return (
      <SubmitButton
        text={<Trans i18nKey="common:save" />}
        onClick={() => {
          this.handleSave();
          onClose();
        }}
      />
    );
  }

  renderMutation() {
    const { mutation, onClose } = this.props;
    return <Mutation mutation={mutation} onClose={onClose} onSave={this.handleSave} />;
  }

  renderStatic() {
    const { classes, data } = this.props;
    const { src } = data;

    return <img src={src} className={classes.static} />;
  }

  renderCropper() {
    const { aspectRatio, classes, data } = this.props;
    const { src } = data;
    if (!src) {
      return null;
    }

    return (
      <Cropper
        ref={this.cropper as any}
        src={src}
        className={classes.cropper}
        aspectRatio={aspectRatio}
        autoCropArea={1}
      />
    );
  }

  render() {
    const { classes, mutation, open, onUpload } = this.props;
    const isGif = this.isGif();

    return (
      <MuiDialog disableBackdropClick open={open}>
        {this.renderTitle()}
        <DialogContent>
          {isGif ? this.renderStatic() : this.renderCropper()}
          <Grid container direction="column" alignItems="center" className={classes.container}>
            <Grid item>{mutation ? this.renderMutation() : this.renderSubmit()}</Grid>
            <Grid item>
              <UploadInput onUpload={onUpload}>
                <Button component="span">
                  <Trans i18nKey="user:replaceImage" />
                </Button>
              </UploadInput>
            </Grid>
          </Grid>
        </DialogContent>
      </MuiDialog>
    );
  }
}

export default enhancers(Dialog);
